/**
 * Viewport math for Presentation Mode — the magnify-and-pan overlay used when
 * demoing 360 on a TV.
 *
 * The transform lives on <body>, whose layout box is frozen to the viewport size
 * captured on entry. Nothing here touches the DOM: `x`/`y` are the CSS
 * `translate()` in screen pixels, applied BEFORE `scale()`, matching
 *
 *   transform: translate(x, y) scale(scale)
 *
 * with `transform-origin: 0 0`. So a content point `c` lands on screen at
 * `x + c * scale`.
 */

export type Viewport = {
  scale: number;
  x: number;
  y: number;
};

export const MIN_SCALE = 1;
export const MAX_SCALE = 8;

export const IDENTITY: Viewport = { scale: 1, x: 0, y: 0 };

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/**
 * Scale by `factor` while pinning the content currently under the screen point
 * (`px`, `py`) — map-style zoom-to-pointer, so aiming at a section and zooming
 * needs no follow-up panning.
 */
export function zoomAt(v: Viewport, factor: number, px: number, py: number): Viewport {
  const scale = clampScale(v.scale * factor);
  // Solve for the translate that keeps the anchor's content point under the
  // cursor. Falls out to x unchanged when the scale was clamped.
  const contentX = (px - v.x) / v.scale;
  const contentY = (py - v.y) / v.scale;
  return {
    scale,
    x: px - contentX * scale,
    y: py - contentY * scale,
  };
}

/**
 * Keep the magnified content covering the screen — you can never drag empty
 * space into view. At scale 1 that pins the pan to the origin, which is what
 * makes exiting the mode a no-op visually.
 */
export function clampPan(v: Viewport, viewportWidth: number, viewportHeight: number): Viewport {
  const minX = viewportWidth * (1 - v.scale);
  const minY = viewportHeight * (1 - v.scale);
  return {
    scale: v.scale,
    x: Math.min(0, Math.max(minX, v.x)),
    y: Math.min(0, Math.max(minY, v.y)),
  };
}
